import { Component, HostListener, OnInit, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { UntilDestroy } from '@ngneat/until-destroy';
import { AppService } from 'src/app/app.service';
import { WalletDialogType, WalletService } from 'src/app/pages/wallet/wallet.service';
import { CSButtonComponent } from 'src/app/shared/components/cs-button/cs-button.component';
import { CSIconButtonComponent } from 'src/app/shared/components/cs-icon-button/cs-icon-button.component';
import { CSInputComponent } from 'src/app/shared/components/cs-input/cs-input.component';
import { CSSelectComponent } from 'src/app/shared/components/cs-select/cs-select.component';
import { PipesModule } from 'src/app/shared/pipes/pipes.module';

@UntilDestroy()
@Component({
  selector: 'add-edit-address',
  standalone: true,
  imports: [CSButtonComponent, CSInputComponent, CSIconButtonComponent, CSSelectComponent, PipesModule],
  templateUrl: './add-edit-address.component.html',
  styleUrl: './add-edit-address.component.scss',
})
export class AddEditAddressComponent implements OnInit {
  constructor(
    private walletService: WalletService,
    private appService: AppService,
  ) {}

  //network variable
  /** 當前選擇的網路 */
  selectedNetwork = '';
  /** 網路列表 */
  networkList: { value: string; label: string; regExp: string; disabled: boolean }[] = [];
  /** 網路加載狀態 */
  networkLoading = false;

  /** 地址文字 */
  address: string = '';
  /** 地址驗證 */
  addressValid = { valid: true, text: '' };
  /** 備註驗證 */
  remark: string = '';
  /** 備註驗證 */
  remarkValid = { valid: true, text: '' };
  /** 備註最大字數 */
  remarkMaxLength = 15;
  /** 備註字數限制文字 */
  remarkLengthText: string = `0/${this.remarkMaxLength}`;

  /** 地址正則表達式 */
  addressRegExp: WritableSignal<string> = signal('');
  /** 地址驗證監聽 */
  addressRegExpWatch$ = toObservable(this.addressRegExp).pipe(takeUntilDestroyed());

  ngOnInit(): void {
    this.getAllNetworks();
    this.addressRegExpWatch$.subscribe(() => {
      this.onAddressChange();
    });
  }

  /** 回到地址列表頁面 */
  goBack() {
    this.walletService.setWalletDialogType(WalletDialogType.SAVED_ADDRESS);
  }

  /** 網路改變 */
  networkChange() {
    const tmp = this.networkList.find(x => x.value === this.selectedNetwork)?.regExp;
    this.addressRegExp.set(tmp || '');
    this.onAddressChange();
  }

  /**
   * 獲取全部網路
   */
  async getAllNetworks() {
    this.networkLoading = true;
    if (this.walletService.allNetworkList.length == 0) {
      await this.walletService.getAllNetworks();
    }
    const tmp = this.walletService.allNetworkList;

    this.networkList = tmp.map(item => {
      return {
        value: item.network,
        label: item.desc,
        regExp: item.addressRegex,
        disabled: false,
      };
    });
    this.networkLoading = false;
  }

  /**
   * 地址輸入
   */
  onAddressChange() {
    // 如果沒有輸入就清空所有狀態
    if (this.address == '') {
      this.addressValid.valid = true;
      this.addressValid.text = '';
      this.checkNetworkValid();
      return;
    }
    const regExp = this.addressRegExp();
    if (regExp) {
      // 根据当前正则验证
      const r = new RegExp(regExp).test(this.address);
      this.checkNetworkValid();
      if (r) {
        this.addressValid.valid = true;
      } else {
        this.addressValid.valid = false;
        this.addressValid.text = this.selectedNetwork
          ? 'Please ensure that the wallet address matches the selected network.'
          : 'Address format incorrect. Please verify its length and format before trying again.';
      }
    } else {
      //数字货币时候如果没有选网络，自动选中首个可能匹配的网络
      if (!this.selectedNetwork) {
        if (this.networkList.length > 0) {
          this.networkList.some(x => {
            const r = new RegExp(x.regExp).test(this.address);
            if (r) {
              this.addressValid.valid = true;
              this.selectedNetwork = x.value;
              this.addressRegExp.set(x.regExp);
              return true;
            } else {
              this.addressValid.valid = false;
              this.addressValid.text = 'Please ensure that the wallet address matches the selected network.';
              return false;
            }
          });
        } else {
          // 沒有可选网络 translate:请检查提现地址，提现地址必须和主网络匹配
          this.addressValid.valid = false;
          this.addressValid.text = 'Please ensure that the wallet address matches the selected network.';
        }
      } else {
        // 重置验证状态
        this.addressValid.valid = false;
        this.addressValid.text = '';
      }
    }
  }

  /**备注输入 */
  onRemarkChange() {
    this.remarkLengthText = `${this.remark.length}/15`;
    this.validateRemark();
  }

  /** 備註驗證 */
  validateRemark() {
    const remarkLength = this.remark.trim().length;
    if (remarkLength === 0) {
      this.remarkValid.valid = true;
      this.remarkValid.text = '';
      return;
    }

    if (remarkLength < 4) {
      this.remarkValid.valid = false;
      this.remarkValid.text = 'Please use at least 4 characters';
      return;
    }

    this.remarkValid.valid = true;
    this.remarkValid.text = '';
  }

  /**
   * 確認網路與地址是否匹配
   *
   * @param data 地址字符串，默認使用當前地址
   */
  checkNetworkValid(data: string = this.address) {
    if (data) {
      this.networkList.forEach(item => {
        item.disabled = !new RegExp(item.regExp).test(this.address);
      });
    } else {
      this.networkList.forEach(item => {
        item.disabled = false;
      });
    }
  }

  /**
   * 是否可以提交
   *
   * @returns {boolean} 返回是否可以提交
   */

  canSubmit() {
    return (
      this.remarkValid.valid &&
      this.remark.trim().length >= 4 &&
      this.selectedNetwork &&
      this.address &&
      this.addressValid.valid
    );
  }

  @HostListener('document:keydown.enter', ['$event'])
  public onKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' && this.canSubmit()) {
      this.submit();
    }
  }

  /** 提交新增地址 */
  submit() {
    this.walletService.otpType = 'AddTokenAddress';
    this.walletService.email = this.appService.userInfo$.getValue()?.email || '';
    this.walletService.addAddressData = {
      address: this.address,
      remark: this.remark,
      network: this.selectedNetwork,
    };
    this.walletService.setWalletDialogType(WalletDialogType.VERIFY_2FA);
  }
}
